import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useConfirmDeleteOption } from '@/features/transaction_items/Detail/hooks/useConfirmDeleteOption';
import {
  TransactionDetailOptionFormType,
  TransactionItemDetailOptionsColumn,
} from '@/features/transaction_items/Detail/type';
import { ButtonV2, IconCheck } from '@moneyforward/ap-frontend-components';
import { ColumnType } from 'antd/es/table/interface';
import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import classNames from 'classnames';
import styles from './column.module.scss';

const cx = classNames.bind(styles);

type Props = {
  onDelete: (id: string, label: string) => void;
  onEdit: (id: string, data: TransactionDetailOptionFormType) => void;
};

export const useTransactionItemDetailOptionsColumns = ({
  onDelete,
  onEdit,
}: Props): {
  columns: ColumnType<TransactionItemDetailOptionsColumn>[];
  DeleteConfirmModal: React.FC;
} => {
  const { t } = useTranslation();
  const { isEdit } = useTransactionItemDetailEdit();
  const { confirm, ConfirmModal: DeleteConfirmModal } =
    useConfirmDeleteOption();

  const onClickDelete = useCallback(
    async (record: TransactionItemDetailOptionsColumn) => {
      const confirmed = await confirm({
        code: record.code,
        label: record.label,
      });
      if (confirmed) {
        onDelete(record.id.toString(), record.label);
      }
    },
    [onDelete, confirm]
  );

  const onClickEdit = useCallback(
    (record: TransactionItemDetailOptionsColumn) => {
      onEdit(record.id.toString(), {
        code: record.code,
        label: record.label,
        priority: record.priority,
        is_visible: record.is_visible,
      });
    },
    [onEdit]
  );

  const columns = useMemo(
    () => [
      {
        title: t('transaction_items.detail.options.code'),
        dataIndex: 'code',
        key: 'code',
        render: (value: string) => {
          return <div className={cx(styles['minw-80'])}>{value}</div>;
        },
        width: 250,
      },
      {
        title: t('transaction_items.detail.options.label'),
        dataIndex: 'label',
        key: 'label',
        render: (value: string) => {
          return <div className={cx(styles['minw-400'])}>{value}</div>;
        },
        width: 400,
      },
      {
        title: t('transaction_items.detail.options.enabled_disabled'),
        dataIndex: 'is_visible',
        key: 'is_visible',
        render(value: boolean) {
          return value ? (
            <div className={cx(styles['enabled-disabled-container'])}>
              {t('transaction_items.detail.options.enabled')}
              <IconCheck size={16} />
            </div>
          ) : (
            t('transaction_items.detail.options.disabled')
          );
        },
        width: 80,
      },
      {
        title: t('transaction_items.detail.options.priority'),
        dataIndex: 'priority',
        key: 'priority',
        width: 80,
      },
      {
        title: ' ',
        key: 'action',
        render: (_: unknown, record: TransactionItemDetailOptionsColumn) => (
          <div className={cx(styles['button-group'])}>
            <ButtonV2
              onClick={() => {
                onClickEdit(record);
              }}
              isSecondary
              isDisabled={isEdit}
            >
              {t('edit')}
            </ButtonV2>
            <ButtonV2
              onClick={() => {
                onClickDelete(record);
              }}
              isSecondary
              color='danger'
              isDisabled={isEdit}
            >
              {t('delete')}
            </ButtonV2>
          </div>
        ),
        width: 160,
      },
    ],
    [t, onClickDelete, onClickEdit, isEdit]
  );

  return useMemo(
    () => ({
      columns,
      DeleteConfirmModal,
    }),
    [columns, DeleteConfirmModal]
  );
};
